import { db } from '../lib/firebase';
import { collection, doc, getDoc, getDocs, setDoc, updateDoc, serverTimestamp, Timestamp } from 'firebase/firestore';
import { Organization } from '../types/organization';
import { User } from '../types/user';
import { OrganizationSubscription, PREDEFINED_PACKAGES, PackageType } from '../types/package';

export interface OnboardingData {
  orgName: string;
  taxNumber?: string;
  industry?: string;
  address: {
    street: string;
    city: string;
    postalCode: string;
    country: string;
  };
  contact: {
    name: string;
    email: string;
    phone: string;
    position: string;
  };
  packageType: PackageType;
  packageId?: string;
}

const TRIAL_DAYS = 14;

export const onboardingService = {
  async createOrganizationWithSubscription(
    userId: string,
    userEmail: string | null,
    userDisplayName: string | null,
    data: OnboardingData
  ): Promise<{ organizationId: string; subscriptionId: string }> {
    // 1. Create organization
    const orgRef = doc(collection(db, 'organizations'));
    const organization: Organization = {
      id: orgRef.id,
      name: data.orgName,
      ownerId: userId,
      taxNumber: data.taxNumber || '',
      registrationNumber: '',
      industry: data.industry || '',
      address: data.address,
      contact: data.contact,
      users: [{
        userId,
        email: userEmail || '',
        role: 'admin',
        addedAt: new Date()
      }],
      isActive: true,
      onboardingCompleted: true,
      createdAt: new Date(),
      updatedAt: new Date()
    };

    await setDoc(orgRef, organization);

    // 2. Use provided packageId or find package by type
    const packageType = data.packageType;
    let packageId = data.packageId || '';
    
    if (!packageId) {
      const packagesQuery = collection(db, 'packages');
      const packagesSnap = await getDocs(packagesQuery);
      
      if (!packagesSnap.empty) {
        const foundPkg = packagesSnap.docs.find(d => {
          const pkgData = d.data();
          return pkgData.type === packageType;
        });
        if (foundPkg) {
          packageId = foundPkg.id;
        }
      }

      // Fallback: if no packages in Firestore, use the predefined one
      if (!packageId) {
        const predefined = PREDEFINED_PACKAGES.find(p => p.type === packageType);
        if (predefined) {
          const pkgRef = doc(collection(db, 'packages'));
          await setDoc(pkgRef, {
            ...predefined,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp()
          });
          packageId = pkgRef.id;
        }
      }
    }

    // 3. Create subscription
    const subRef = doc(collection(db, 'subscriptions'));
    const now = new Date();
    const trialEnd = new Date();
    trialEnd.setDate(trialEnd.getDate() + TRIAL_DAYS);

    const isFree = packageType === 'free';
    const subscription: Omit<OrganizationSubscription, 'id'> = {
      organizationId: orgRef.id,
      packageId,
      status: isFree ? 'active' : 'trial',
      startDate: now,
      endDate: isFree ? undefined : trialEnd,
      trialEndDate: isFree ? undefined : trialEnd,
      autoRenew: !isFree,
      usage: {
        users: 1,
        storage: 0,
        apiCalls: 0,
        lastUpdated: now
      },
      createdAt: now,
      updatedAt: now
    };

    await setDoc(subRef, {
      ...subscription,
      startDate: Timestamp.fromDate(now),
      endDate: isFree ? null : Timestamp.fromDate(trialEnd),
      trialEndDate: isFree ? null : Timestamp.fromDate(trialEnd),
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      usage: {
        ...subscription.usage,
        lastUpdated: Timestamp.fromDate(now)
      }
    });

    // 4. Update user with organizationId and onboardingCompleted
    const userRef = doc(db, 'users', userId);
    await updateDoc(userRef, {
      organizationId: orgRef.id,
      role: 'admin',
      onboardingCompleted: true,
      updatedAt: new Date()
    });

    return {
      organizationId: orgRef.id,
      subscriptionId: subRef.id
    };
  },

  async checkOnboardingStatus(userId: string): Promise<boolean> {
    const userRef = doc(db, 'users', userId);
    const userSnap = await getDoc(userRef);
    if (!userSnap.exists()) return false;
    return userSnap.data().onboardingCompleted === true;
  }
};
